// ═══════════════════════════════════════════════════════════════════
//  TOQUM · TEAM Module
//  Profiles · Allocated suppliers/locations · Performance summary ·
//  Visit plans · Inspection schedule/history · Action plans ·
//  Follow-up reminders · Personal KPI picker for Dashboard
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useMemo } from 'react';
import { T, Page, CardRow, Split, uid, today, addDays, num, Section, Field, Grid, Btn, DataTable,
         TabBar, Tag, Badge, StatCard, Collapse, DynamicTable, printHTML, isManager, isQAOnly, visibleSuppliers } from './Shared';

export const KPI_CATALOG = [
  'Inspections Planned vs Done', 'First-Pass Rate %', 'DHU (Defects per Hundred Units)',
  'Exceptions Raised', 'Exceptions Approved', 'Factory Visits Completed',
  'CAP Closure Rate %', 'Supplier Audit Grade Avg', 'Major Defect Suppliers',
  'Inspection Pass Rate %', 'Pending Follow-ups', 'Claims Open vs Settled',
  'On-time Inspection Offer %', 'Warning Letters Issued', 'Blacklisted Suppliers',
];

export default function TeamModule({ db, setDb, saveToDB, currentUser }) {
  const TABS = ['TEAM DIRECTORY','MY PROFILE','ACTION PLANS','VISIT PLANNER','REMINDERS','MY KPIs'];
  const [tab, setTab] = useState('TEAM DIRECTORY');
  const [viewMember, setViewMember] = useState(null);

  const users = (db.users||[]).filter(u => u.status==='Active');
  const myAllocations = (m) => (db.qaAllocations||[]).filter(a => a.qaMember===m.name);
  const supplierOf = (name) => (db.suppliers||[]).find(s => s.name===name);

  // ── Performance computation for a member ──
  const perf = (member) => {
    const insp = (db.inspections||[]).filter(i => i.qaMember===member.name);
    const done = insp.filter(i => ['Passed','Failed'].includes(i.result));
    const passed = insp.filter(i => i.result==='Passed');
    const exceptions = (db.exceptions||[]).filter(e => e.raisedBy===member.name);
    const visits = (db.visitPlans||[]).filter(v => v.member===member.name);
    const suppliers = myAllocations(member).map(a => a.supplier);
    const supDefects = {};
    insp.forEach(i => {
      const d = (i.defects||[]).reduce((a,b)=>a + (b.critical||0)+(b.major||0),0);
      supDefects[i.supplier] = (supDefects[i.supplier]||0) + d;
    });
    const majorDefectSuppliers = Object.entries(supDefects).filter(([,v])=>v>0).sort((a,b)=>b[1]-a[1]);
    return { insp, done, passed, exceptions, visits, suppliers, majorDefectSuppliers,
      passRate: done.length ? (passed.length/done.length*100).toFixed(1) : '—' };
  };

  // QA-only: restrict visible team members to manager(s) + self only
  const visibleUsers = isQAOnly(currentUser)
    ? users.filter(u => u.id === currentUser.id || isManager(u))
    : users;

  // ── Team Directory ──
  const Directory = () => (
    <Section title={isQAOnly(currentUser) ? 'My Manager & Profile' : 'Quality Team Members'}>
      {isQAOnly(currentUser) && (
        <div style={{ fontSize:11.5, color:T.textSub, marginBottom:10,
          background:`${T.primary}0d`, border:`1px solid ${T.primary}22`,
          borderRadius:6, padding:'8px 12px' }}>
          As a QA you can see your manager's profile and your own. Supply chain information is not visible at your access level.
        </div>
      )}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:14 }}>
        {visibleUsers.map(u => {
          const allocs = myAllocations(u);
          const locations = [...new Set(allocs.map(a => supplierOf(a.supplier)?.city).filter(Boolean))];
          return (
            <div key={u.id} onClick={()=>setViewMember(u)}
              style={{ background:'white', border:`1px solid ${T.border}`, borderRadius:10, padding:15, cursor:'pointer', boxShadow:T.cardShadow }}>
              <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:8 }}>
                <div style={{ width:42, height:42, borderRadius:'50%', background:T.primary, color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:16 }}>
                  {u.name.split(' ').map(w=>w[0]).join('').slice(0,2)}
                </div>
                <div>
                  <div style={{ fontWeight:800, fontSize:13 }}>{u.name}</div>
                  <div style={{ fontSize:10.5, color:T.textSub, textTransform:'uppercase' }}>{u.role}</div>
                </div>
              </div>
              {!isQAOnly(currentUser) && (
                <>
                  <div style={{ fontSize:11, color:T.textSub }}>📍 {locations.length ? locations.join(', ') : 'No location allocated'}</div>
                  <div style={{ marginTop:5 }}>
                    {allocs.slice(0,3).map(a => <Tag key={a.id} label={a.supplier} color={T.teal} />)}
                    {allocs.length>3 && <Tag label={`+${allocs.length-3} more`} color={T.textSub} />}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );

  // ── Profile / performance view ──
  const Profile = ({ member }) => {
    const p = perf(member);
    const schedule = p.insp.filter(i => i.status==='Scheduled' || i.result==='Pending');
    const history  = p.insp.filter(i => ['Passed','Failed'].includes(i.result));
    const plans = (db.actionPlans||[]).filter(a => p.suppliers.includes(a.supplier));
    return (
      <div>
        <Section title={`Profile — ${member.name} (${member.role})`}
          actions={viewMember && <Btn small color="#64748b" label="← Back to team" onClick={()=>setViewMember(null)} />}>
          <Grid cols={5} gap={12}>
            <StatCard label="Allocated Suppliers" value={p.suppliers.length} color={T.primary} />
            <StatCard label="Inspections Done" value={p.done.length} sub={`${schedule.length} scheduled`} color={T.teal} />
            <StatCard label="Pass Rate" value={`${p.passRate}%`} color={T.success} />
            <StatCard label="Exceptions Raised" value={p.exceptions.length} color={T.warning} />
            <StatCard label="Visits Planned" value={p.visits.length} sub={`${p.visits.filter(v=>v.status==='Completed').length} completed`} color={T.purple} />
          </Grid>
          <div style={{ marginTop:12 }}>
            <b style={{ fontSize:12 }}>Allocated Suppliers:</b>{' '}
            {p.suppliers.length ? p.suppliers.map(s => <Tag key={s} label={s} color={T.teal} />) : <span style={{ fontSize:12, color:T.textSub }}>None yet — allocation done by Manager in Admin → Allocation.</span>}
          </div>
        </Section>

        <Grid cols={2} gap={16}>
          <Section title="Inspection Schedule (upcoming)">
            <DataTable columns={['Date','Supplier','PO','Stage']} rows={schedule}
              renderCell={(r,c)=>({ Date:r.date, Supplier:r.supplier, PO:r.poNo, Stage:r.stage }[c])} />
          </Section>
          <Section title="Inspection History">
            <DataTable columns={['Date','Supplier','PO','Stage','Result']} rows={history.slice(-15).reverse()}
              renderCell={(r,c)=>({ Date:r.date, Supplier:r.supplier, PO:r.poNo, Stage:r.stage, Result:<Badge status={r.result}/> }[c])} />
          </Section>
        </Grid>

        <Grid cols={2} gap={16}>
          <Section title="Major Defect Suppliers (from inspection findings)">
            <DataTable columns={['Supplier','Critical + Major Defects']} rows={p.majorDefectSuppliers.map(([s,v],i)=>({id:i,s,v}))}
              renderCell={(r,c)=>({ 'Supplier':r.s, 'Critical + Major Defects':<b style={{color:r.v>10?T.danger:T.warning}}>{r.v}</b> }[c])}
              emptyMsg="No major defects recorded — good sign." />
          </Section>
          <Section title="Supplier-wise Action Plans">
            <DataTable columns={['Supplier','Action','Due','Status']} rows={plans}
              renderCell={(r,c)=>({ Supplier:r.supplier, Action:r.action, Due:r.dueDate, Status:<Badge status={r.status}/> }[c])} />
          </Section>
        </Grid>
      </div>
    );
  };

  // ── Visit Planner (weekly/monthly factory visit plans) ──
  const VisitPlanner = () => {
    const [f, setF] = useState({ supplier:'', date:today(), purpose:'Routine Visit', period:'Weekly' });
    const mySuppliers = isManager(currentUser)
      ? (db.suppliers||[]).map(s=>s.name)
      : myAllocations(currentUser).map(a => a.supplier);
    const myVisits = (db.visitPlans||[]).filter(v => v.member===currentUser.name);
    const add = async () => {
      if (!f.supplier || !f.date) return;
      const entry = { id: uid(), member: currentUser.name, status:'Scheduled', ...f };
      setDb(p => ({ ...p, visitPlans:[...(p.visitPlans||[]), entry] }));
      await saveToDB('visitPlan', entry);
    };
    const mark = (v, status) => setDb(p => ({ ...p, visitPlans:p.visitPlans.map(x => x.id===v.id ? {...x,status} : x) }));
    return (
      <Section title="My Weekly / Monthly Factory Visit Plan">
        <Grid cols={5}>
          <Field label="Supplier / Factory" value={f.supplier} onChange={e=>setF({...f,supplier:e.target.value})} options={mySuppliers} />
          <Field label="Visit Date" type="date" value={f.date} onChange={e=>setF({...f,date:e.target.value})} />
          <Field label="Plan Period" value={f.period} onChange={e=>setF({...f,period:e.target.value})} options={['Weekly','Monthly']} />
          <Field label="Purpose" value={f.purpose} onChange={e=>setF({...f,purpose:e.target.value})}
            options={['Routine Visit','Inline Inspection','Pre-Final Inspection','Final Inspection','Audit','CAP Follow-up','PPM','Claim Verification']} />
          <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:10 }}><Btn label="+ Plan Visit" onClick={add} /></div>
        </Grid>
        <DataTable columns={['Date','Supplier','Period','Purpose','Status']} rows={myVisits.sort((a,b)=>a.date.localeCompare(b.date))}
          renderCell={(r,c)=>({ Date:r.date, Supplier:r.supplier, Period:r.period, Purpose:r.purpose, Status:<Badge status={r.status}/> }[c])}
          actions={r => r.status==='Scheduled' && <span style={{display:'flex',gap:5}}>
            <Btn small color={T.success} label="Done" onClick={()=>mark(r,'Completed')} />
            <Btn small outline color={T.danger} label="Cancel" onClick={()=>mark(r,'Rejected')} />
          </span>} />
      </Section>
    );
  };

  // ── Follow-up reminders ──
  const Reminders = () => {
    const [f, setF] = useState({ supplier:'', note:'', dueDate:today(), repeat:'None' });
    const mine = (db.reminders||[]).filter(r => r.member===currentUser.name);
    const overdue = mine.filter(r => r.status==='Open' && r.dueDate < today());
    const add = async () => {
      if (!f.note) return;
      const entry = { id: uid(), member: currentUser.name, status:'Open', ...f };
      setDb(p => ({ ...p, reminders:[...(p.reminders||[]), entry] }));
      await saveToDB('reminder', entry);
      setF({ supplier:'', note:'', dueDate:today(), repeat:'None' });
    };
    return (
      <Section title={`Follow-up Reminders ${overdue.length ? `— ⚠ ${overdue.length} overdue` : ''}`}>
        <Grid cols={5}>
          <Field label="Supplier (optional)" value={f.supplier} onChange={e=>setF({...f,supplier:e.target.value})} options={(db.suppliers||[]).map(s=>s.name)} />
          <Field label="Reminder Note" value={f.note} onChange={e=>setF({...f,note:e.target.value})} placeholder="e.g. Follow up CAP #12" style={{gridColumn:'span 2'}} />
          <Field label="Due Date" type="date" value={f.dueDate} onChange={e=>setF({...f,dueDate:e.target.value})} />
          <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:10 }}><Btn label="+ Add" onClick={add} /></div>
        </Grid>
        <DataTable columns={['Due','Supplier','Note','Status']} rows={mine.sort((a,b)=>a.dueDate.localeCompare(b.dueDate))}
          renderCell={(r,c)=>({ Due:<span style={{color: r.status==='Open'&&r.dueDate<today()?T.danger:'inherit', fontWeight:700}}>{r.dueDate}</span>,
            Supplier:r.supplier||'—', Note:r.note, Status:<Badge status={r.status}/> }[c])}
          actions={r => r.status==='Open' && <Btn small color={T.success} label="Close" onClick={()=>setDb(p=>({...p,reminders:p.reminders.map(x=>x.id===r.id?{...x,status:'Closed'}:x)}))} />} />
      </Section>
    );
  };

  // ── Personal KPI picker ──
  const KpiPicker = () => {
    const myPref = (db.kpiPrefs||[]).find(k => k.member===currentUser.name) || { member:currentUser.name, kpis:[] };
    const toggle = async (kpi) => {
      const kpis = myPref.kpis.includes(kpi) ? myPref.kpis.filter(k=>k!==kpi) : [...myPref.kpis, kpi];
      const entry = { ...myPref, id: myPref.id||uid(), kpis };
      setDb(p => ({ ...p, kpiPrefs:[...(p.kpiPrefs||[]).filter(x=>x.member!==currentUser.name), entry] }));
      await saveToDB('kpiPref', entry);
    };
    return (
      <Section title="Choose the KPIs you want on YOUR Dashboard">
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
          {KPI_CATALOG.map(k => (
            <label key={k} style={{ display:'flex', gap:8, alignItems:'center', fontSize:12.5, padding:'8px 10px',
              border:`1.5px solid ${myPref.kpis.includes(k)?T.primary:T.border}`, borderRadius:6, cursor:'pointer',
              background: myPref.kpis.includes(k)?'#0073ea10':'white' }}>
              <input type="checkbox" checked={myPref.kpis.includes(k)} onChange={()=>toggle(k)} /> {k}
            </label>
          ))}
        </div>
        <div style={{ marginTop:10, fontSize:11.5, color:T.textSub }}>Selected KPIs appear in the "My KPIs" strip on the Dashboard.</div>
      </Section>
    );
  };

  // ═══ ACTION PLANS — supplier-wise, driven by failures and their CAPs ═══
  const ActionPlans = () => {
    const plans = db.actionPlans || [];
    const caps  = db.caps || [];
    const [f, setF] = useState({ supplier:'', source:'Inspection Failure', sourceRef:'',
      issue:'', action:'', responsible:currentUser.name, dueDate:addDays(today(),14),
      priority:'High', status:'Open', reminder:true });

    // Failures that have no action plan yet — the reason this page exists
    const openFailures = useMemo(() => {
      const out = [];
      (db.inspections||[]).filter(i=>i.result==='Failed').forEach(i => {
        if (!plans.some(p=>p.sourceRef===`INSP-${i.id}`)) out.push({
          id:`INSP-${i.id}`, kind:'Inspection Failure', supplier:i.supplier, date:i.date,
          detail:`${i.stage} on PO ${i.poNo} — ${i.totals?.critical||0} critical / ${i.totals?.major||0} major`,
          poNo:i.poNo });
      });
      caps.filter(c=>c.status!=='Closed').forEach(c => {
        (c.items||[]).filter(it=>it.status!=='Verified Closed').forEach((it,ix) => {
          const ref = `CAP-${c.id}-${ix}`;
          if (!plans.some(p=>p.sourceRef===ref)) out.push({
            id:ref, kind:'Open CAP Finding', supplier:c.supplier, date:c.createdOn,
            detail:`${it.severity||'Major'} — ${(it.finding||'').slice(0,90)}`,
            due:it.dueDate });
        });
      });
      (db.customerComplaints||[]).filter(c=>c.status!=='Closed').forEach(c => {
        const ref = `CC-${c.id}`;
        if (!plans.some(p=>p.sourceRef===ref)) out.push({
          id:ref, kind:'Customer Complaint', supplier:c.supplier, date:c.date,
          detail:`${c.complaintType} — ${num(c.affectedPcs)} pcs, $${num(c.claimAmount).toLocaleString()} claim` });
      });
      return out;
    }, [db, plans, caps]);

    const add = async (seed) => {
      const src = seed || f;
      if (!src.supplier || !src.issue) return alert('Supplier and issue are required');
      const entry = { id: uid(), supplier:src.supplier, source:src.source||src.kind,
        sourceRef:src.sourceRef||src.id||'', issue:src.issue||src.detail, action:src.action||'',
        responsible:src.responsible||currentUser.name, dueDate:src.dueDate||addDays(today(),14),
        priority:src.priority||'High', status:'Open', createdBy:currentUser.name, createdOn:today(),
        member:currentUser.name, log:[] };
      setDb(p=>({ ...p, actionPlans:[...plans, entry] }));
      await saveToDB('actionPlan', entry);

      // A follow-up reminder is what makes these actually get chased
      if (src.reminder !== false) {
        const rem = { id: uid(), member:currentUser.name, supplier:entry.supplier,
          note:`Follow up: ${entry.issue.slice(0,60)}`, dueDate:entry.dueDate,
          status:'Open', linkedPlan:entry.id, repeat:'None' };
        setDb(p=>({ ...p, reminders:[...(p.reminders||[]), rem] }));
        await saveToDB('reminder', rem);
      }
      if (!seed) setF({ supplier:'', source:'Inspection Failure', sourceRef:'', issue:'', action:'',
        responsible:currentUser.name, dueDate:addDays(today(),14), priority:'High', status:'Open', reminder:true });
    };

    const update = async (plan, patch) => {
      const upd = { ...plan, ...patch,
        log:[...(plan.log||[]), { date:today(), event:`Status → ${patch.status||'updated'}`, by:currentUser.name }] };
      setDb(p=>({ ...p, actionPlans: plans.map(x=>x.id===plan.id?upd:x) }));
      await saveToDB('actionPlan', upd);
    };

    const bySupplier = [...new Set(plans.map(p=>p.supplier))].map(sup => {
      const ps = plans.filter(p=>p.supplier===sup);
      return { id:sup, supplier:sup, total:ps.length,
        open:ps.filter(p=>p.status==='Open').length,
        overdue:ps.filter(p=>p.status!=='Closed' && p.dueDate<today()).length,
        closed:ps.filter(p=>p.status==='Closed').length };
    });

    const printPlans = (sup) => {
      const ps = plans.filter(p=>p.supplier===sup);
      printHTML(`Action Plan — ${sup}`, `
        <h1>TOQUM · Supplier Action Plan</h1>
        <table><tr><td><b>Supplier:</b> ${sup}</td><td><b>Printed:</b> ${today()}</td>
          <td><b>By:</b> ${currentUser.name}</td></tr></table>
        <table><tr><th>#</th><th>Source</th><th>Issue / Failure</th><th>Action Agreed</th>
          <th>Responsible</th><th>Due</th><th>Priority</th><th>Status</th></tr>
          ${ps.map((p,i)=>`<tr><td>${i+1}</td><td>${p.source}</td><td>${p.issue}</td>
            <td>${p.action||''}</td><td>${p.responsible}</td><td>${p.dueDate}</td>
            <td>${p.priority}</td><td>${p.status}</td></tr>`).join('')}</table>
        <table class="sign"><tr><th>QA</th><th>Supplier Representative</th><th>Quality Manager</th></tr>
          <tr><td>${currentUser.name}</td><td></td><td></td></tr></table>`);
    };

    return (
      <div>
        <CardRow min={190}>
          <StatCard label="Action Plans" value={plans.length} color={T.primary} />
          <StatCard label="Open" value={plans.filter(p=>p.status==='Open').length} color={T.warning} />
          <StatCard label="Overdue"
            value={plans.filter(p=>p.status!=='Closed' && p.dueDate<today()).length} color={T.danger} />
          <StatCard label="Closed" value={plans.filter(p=>p.status==='Closed').length} color={T.success} />
          <StatCard label="Failures Without a Plan" value={openFailures.length}
            color={openFailures.length?T.danger:T.success} />
        </CardRow>

        {openFailures.length>0 && (
          <Section title="⚠ Failures and open CAP findings with no action plan yet">
            <DataTable columns={['Source','Supplier','Date','Detail']} rows={openFailures}
              renderCell={(r,c)=>({
                Source:<Tag label={r.kind} color={r.kind.includes('CAP')?T.purple:r.kind.includes('Customer')?T.orange:T.danger} />,
                Supplier:<b>{r.supplier}</b>, Date:r.date, Detail:r.detail }[c])}
              actions={r => <Btn small color={T.warning} label="Create Action Plan"
                onClick={()=>add({ ...r, sourceRef:r.id, source:r.kind, issue:r.detail,
                  dueDate:r.due||addDays(today(),14) })} />} />
          </Section>
        )}

        <Section title="Add Action Plan Manually">
          <Grid cols={4}>
            <Field label="Supplier" value={f.supplier} onChange={e=>setF({...f,supplier:e.target.value})}
              options={(db.suppliers||[]).map(s=>s.name)} />
            <Field label="Source" value={f.source} onChange={e=>setF({...f,source:e.target.value})}
              options={['Inspection Failure','Open CAP Finding','Customer Complaint','Audit Finding','Sample Rejection','Other']} />
            <Field label="Issue / Failure" value={f.issue} onChange={e=>setF({...f,issue:e.target.value})} />
            <Field label="Action Agreed" value={f.action} onChange={e=>setF({...f,action:e.target.value})} />
            <Field label="Responsible" value={f.responsible} onChange={e=>setF({...f,responsible:e.target.value})} />
            <Field label="Due Date" type="date" value={f.dueDate} onChange={e=>setF({...f,dueDate:e.target.value})} />
            <Field label="Priority" value={f.priority} onChange={e=>setF({...f,priority:e.target.value})}
              options={['Critical','High','Medium','Low']} />
            <div style={{ display:'flex', alignItems:'flex-end', gap:10, paddingBottom:10, flexWrap:'wrap' }}>
              <label style={{ fontSize:11.5 }}>
                <input type="checkbox" checked={f.reminder} onChange={e=>setF({...f,reminder:e.target.checked})} />
                {' '}Set follow-up reminder</label>
              <Btn small label="+ Add" onClick={()=>add(null)} />
            </div>
          </Grid>
        </Section>

        <Section title="Supplier-wise Action Plan Summary">
          <DataTable columns={['Supplier','Total','Open','Overdue','Closed','Closure %']} rows={bySupplier}
            renderCell={(r,c)=>({ Supplier:<b>{r.supplier}</b>, Total:r.total, Open:r.open,
              Overdue: r.overdue ? <b style={{ color:T.danger }}>{r.overdue}</b> : '0',
              Closed:r.closed,
              'Closure %':<b style={{ color: r.total && r.closed/r.total>=0.8 ? T.success : T.warning }}>
                {r.total ? Math.round(r.closed/r.total*100) : 0}%</b> }[c])}
            actions={r => <Btn small outline label="🖨 Print" onClick={()=>printPlans(r.supplier)} />}
            emptyMsg="No action plans yet." />
        </Section>

        <Section title="Action Plan Register">
          <DataTable columns={['Created','Supplier','Source','Issue','Action','Responsible','Due','Priority','Status']}
            rows={plans.slice().reverse()}
            renderCell={(r,c)=>({
              Created:r.createdOn, Supplier:r.supplier,
              Source:<Tag label={r.source} color={T.teal} />, Issue:r.issue, Action:r.action||'—',
              Responsible:r.responsible,
              Due:<span style={{ color: r.status!=='Closed' && r.dueDate<today() ? T.danger : 'inherit',
                fontWeight:700 }}>{r.dueDate}</span>,
              Priority:<Badge status={r.priority==='Critical'?'Failed':r.priority==='High'?'Pending':'Draft'} />,
              Status:<select value={r.status} onChange={e=>update(r,{ status:e.target.value })}
                style={{ fontSize:11, padding:3 }}>
                {['Open','In Progress','Awaiting Supplier','Verified','Closed'].map(o=><option key={o}>{o}</option>)}
              </select>,
            }[c])}
            emptyMsg="No action plans recorded." />
        </Section>
      </div>
    );
  };

  return (
    <Page title="QUALITY TEAM" subtitle="Profiles · allocations · visit plans · action plans · reminders · KPIs">
      <TabBar tabs={TABS} active={tab} onChange={t=>{setTab(t);setViewMember(null);}} />
      {tab==='TEAM DIRECTORY' && (viewMember ? <Profile member={viewMember} /> : <Directory />)}
      {tab==='MY PROFILE' && <Profile member={currentUser} />}
      {tab==='ACTION PLANS' && <ActionPlans />}
      {tab==='VISIT PLANNER' && <VisitPlanner />}
      {tab==='REMINDERS' && <Reminders />}
      {tab==='MY KPIs' && <KpiPicker />}
    </Page>
  );
}
